// src/pages/Partner/PartnerSettings.jsx
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  BadgeCheck,
  Building2,
  CalendarDays,
  FileText,
  Globe2,
  Hash,
  LockKeyhole,
  Mail,
  MapPin,
  Phone,
  RefreshCw,
  RotateCcw,
  Save,
  ShieldCheck,
  UserCircle,
} from "lucide-react";
import { useOutletContext } from "react-router-dom";

import { supabase } from "../../supabaseClient";

import {
  fetchPartnerSettings,
  updatePartnerSettings,
} from "../../lib/partner/partnerSettings";

const EMPTY_DRAFT = {
  name: "",
  description: "",
  contact_email: "",
  contact_phone: "",
  website: "",
  address: "",
};

function settingsToDraft(settings) {
  return {
    name:
      settings?.name || "",

    description:
      settings?.description || "",

    contact_email:
      settings?.contact_email || "",

    contact_phone:
      settings?.contact_phone || "",

    website:
      settings?.website || "",

    address:
      settings?.address || "",
  };
}

function formatRole(role) {
  return String(
    role || "viewer"
  )
    .split("_")
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1)
    )
    .join(" ");
}

function formatDateTime(value) {
  if (!value) {
    return "Not available";
  }

  const date =
    new Date(value);

  if (
    Number.isNaN(
      date.getTime()
    )
  ) {
    return String(value);
  }

  return new Intl.DateTimeFormat(
    "en-ZA",
    {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }
  ).format(date);
}

function statusClasses(status) {
  switch (status) {
    case "active":
      return "bg-green-100 text-green-800";

    case "suspended":
      return "bg-red-100 text-red-700";

    case "archived":
      return "bg-gray-200 text-gray-700";

    default:
      return "bg-amber-100 text-amber-800";
  }
}

function isValidEmail(value) {
  const cleanValue =
    String(
      value || ""
    ).trim();

  if (!cleanValue) {
    return true;
  }

  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
    cleanValue
  );
}

function isValidWebsite(value) {
  const cleanValue =
    String(
      value || ""
    ).trim();

  if (!cleanValue) {
    return true;
  }

  try {
    const urlValue =
      /^https?:\/\//i.test(
        cleanValue
      )
        ? cleanValue
        : `https://${cleanValue}`;

    const url =
      new URL(
        urlValue
      );

    return Boolean(
      url.hostname
    );
  } catch {
    return false;
  }
}

function nameFromUser(user) {
  const metadata =
    user?.user_metadata || {};

  return (
    String(
      metadata.full_name ||
        ""
    ).trim() ||
    String(
      metadata.name ||
        ""
    ).trim() ||
    String(
      metadata.display_name ||
        ""
    ).trim()
  );
}

export default function PartnerSettings() {
  const {
    access,
    onPartnerUpdated,
  } = useOutletContext();

  const [
    settings,
    setSettings,
  ] = useState(null);

  const [
    draft,
    setDraft,
  ] = useState(
    EMPTY_DRAFT
  );

  const [
    signedInEmail,
    setSignedInEmail,
  ] = useState("");

  const [
    profileName,
    setProfileName,
  ] = useState("");

  const [
    savedProfileName,
    setSavedProfileName,
  ] = useState("");

  const [
    profileSaving,
    setProfileSaving,
  ] = useState(false);

  const [
    profileError,
    setProfileError,
  ] = useState("");

  const [
    profileMessage,
    setProfileMessage,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    saving,
    setSaving,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  const [
    message,
    setMessage,
  ] = useState("");

  const partnerId =
    access?.partner_id || "";

  const role =
    access?.partner_role || "";

  const isFacilitator =
    role ===
    "facilitator";

  const canViewOrganisation =
    !isFacilitator;

  const canManageOrganisation =
    Boolean(settings?.can_manage);

  const loadSettings =
    useCallback(
      async () => {
        if (!partnerId) {
          setError(
            "The partner organisation could not be identified."
          );

          setLoading(false);

          return;
        }

        try {
          setLoading(true);
          setError("");
          setMessage("");
          setProfileError("");
          setProfileMessage("");

          const [
            userResult,
            settingsRow,
          ] =
            await Promise.all([
              supabase.auth.getUser(),

              canViewOrganisation
                ? fetchPartnerSettings(
                    partnerId
                  )
                : Promise.resolve(
                    null
                  ),
            ]);

          if (
            userResult?.error
          ) {
            throw userResult.error;
          }

          const user =
            userResult?.data?.user;

          const currentName =
            nameFromUser(
              user
            );

          setSignedInEmail(
            user?.email ||
              ""
          );

          setProfileName(
            currentName
          );

          setSavedProfileName(
            currentName
          );

          if (
            canViewOrganisation
          ) {
            setSettings(
              settingsRow
            );

            setDraft(
              settingsToDraft(
                settingsRow
              )
            );
          } else {
            setSettings(
              null
            );

            setDraft(
              EMPTY_DRAFT
            );
          }
        } catch (
          loadError
        ) {
          console.error(
            "Partner settings loading failed:",
            loadError
          );

          setError(
            loadError?.message ||
              "The settings could not be loaded."
          );
        } finally {
          setLoading(false);
        }
      },
      [
        partnerId,
        canViewOrganisation,
      ]
    );

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const isDirty =
    useMemo(() => {
      if (!settings) {
        return false;
      }

      const original =
        settingsToDraft(
          settings
        );

      return Object.keys(
        EMPTY_DRAFT
      ).some(
        (key) =>
          String(
            draft[key] ||
              ""
          ) !==
          String(
            original[key] ||
              ""
          )
      );
    }, [
      draft,
      settings,
    ]);

  const isProfileDirty =
    profileName.trim() !==
    savedProfileName.trim();

  function handleChange(
    event
  ) {
    const {
      name,
      value,
    } = event.target;

    setDraft(
      (current) => ({
        ...current,
        [name]: value,
      })
    );

    setError("");
    setMessage("");
  }

  function handleReset() {
    setDraft(
      settingsToDraft(
        settings
      )
    );

    setError("");
    setMessage("");
  }

  async function handleProfileSubmit(
    event
  ) {
    event.preventDefault();

    const cleanName =
      profileName.trim();

    if (!cleanName) {
      setProfileError(
        "Please enter your full name."
      );

      return;
    }

    if (
      cleanName.length >
      100
    ) {
      setProfileError(
        "Your name cannot exceed 100 characters."
      );

      return;
    }

    try {
      setProfileSaving(
        true
      );

      setProfileError(
        ""
      );

      setProfileMessage(
        ""
      );

      const {
        data,
        error:
          updateError,
      } =
        await supabase.auth.updateUser({
          data: {
            full_name:
              cleanName,
          },
        });

      if (
        updateError
      ) {
        throw updateError;
      }

      const savedName =
        nameFromUser(
          data?.user
        ) ||
        cleanName;

      setProfileName(
        savedName
      );

      setSavedProfileName(
        savedName
      );

      setProfileMessage(
        "Your name was updated successfully."
      );
    } catch (
      saveError
    ) {
      console.error(
        "Partner profile update failed:",
        saveError
      );

      setProfileError(
        saveError?.message ||
          "Your name could not be updated."
      );
    } finally {
      setProfileSaving(
        false
      );
    }
  }

  async function handleSubmit(
    event
  ) {
    event.preventDefault();

    if (
      !canManageOrganisation
    ) {
      setError(
        "You do not have permission to update this organisation."
      );

      return;
    }

    const cleanName =
      draft.name.trim();

    if (!cleanName) {
      setError(
        "Organisation name is required."
      );

      return;
    }

    if (
      cleanName.length >
      150
    ) {
      setError(
        "Organisation name cannot exceed 150 characters."
      );

      return;
    }

    if (
      draft.description
        .trim()
        .length >
      2000
    ) {
      setError(
        "Organisation description cannot exceed 2000 characters."
      );

      return;
    }

    if (
      !isValidEmail(
        draft.contact_email
      )
    ) {
      setError(
        "Please enter a valid contact email address."
      );

      return;
    }

    if (
      !isValidWebsite(
        draft.website
      )
    ) {
      setError(
        "Please enter a valid website address."
      );

      return;
    }

    try {
      setSaving(true);
      setError("");
      setMessage("");

      const updatedSettings =
        await updatePartnerSettings(
          partnerId,
          draft
        );

      setSettings(
        updatedSettings
      );

      setDraft(
        settingsToDraft(
          updatedSettings
        )
      );

      onPartnerUpdated?.(
        updatedSettings
      );

      setMessage(
        "Organisation settings updated successfully."
      );
    } catch (
      saveError
    ) {
      console.error(
        "Partner settings update failed:",
        saveError
      );

      setError(
        saveError?.message ||
          "The organisation settings could not be updated."
      );
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-[420px] items-center justify-center rounded-[28px] border border-[#FFE1CF] bg-white">
        <div className="text-center">
          <RefreshCw className="mx-auto h-6 w-6 animate-spin text-[#FF914D]" />

          <p className="mt-3 text-sm font-bold text-[#757575]">
            Loading settings…
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      <header className="rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm md:p-8">
        <div className="flex flex-col justify-between gap-5 lg:flex-row lg:items-start">
          <div className="max-w-3xl">
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Partner · Settings
            </p>

            <h1 className="mt-2 text-[34px] font-black tracking-[-0.03em] text-[#333333] md:text-[42px]">
              Settings
            </h1>

            <p className="mt-3 text-sm leading-relaxed text-[#757575] md:text-base">
              Manage your personal account details
              {canViewOrganisation
                ? " and organisation information."
                : "."}
            </p>
          </div>

          <button
            type="button"
            onClick={loadSettings}
            disabled={
              saving ||
              profileSaving
            }
            className="inline-flex items-center justify-center self-start rounded-full border border-[#DDDDDD] bg-white px-4 py-2.5 text-sm font-black text-[#555555] transition hover:border-[#FF914D] hover:bg-[#FFF4EC] hover:text-[#E9782F] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
        </div>
      </header>

      {error && (
        <div className="mt-5 rounded-[22px] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
          {error}
        </div>
      )}

      <section className="mt-6 rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm lg:p-8">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
            <UserCircle className="h-5 w-5" />
          </div>

          <div>
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Account
            </p>

            <h2 className="mt-1 text-xl font-black text-[#333333]">
              My profile
            </h2>

            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-[#757575]">
              Manage the name shown to other Gwin users where your
              Partner role is relevant.
            </p>
          </div>
        </div>

        {profileMessage && (
          <div className="mt-5 flex items-start rounded-2xl border border-green-200 bg-green-50 p-4 text-sm font-semibold text-green-800">
            <BadgeCheck className="mr-3 mt-0.5 h-5 w-5 flex-none" />

            {profileMessage}
          </div>
        )}

        {profileError && (
          <div className="mt-5 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
            {profileError}
          </div>
        )}

        <form
          onSubmit={
            handleProfileSubmit
          }
          className="mt-6"
        >
          <div className="grid gap-6 md:grid-cols-2">
            <label className="block">
              <span className="text-sm font-black text-[#333333]">
                Full name
                <span className="ml-1 text-red-600">
                  *
                </span>
              </span>

              <div className="relative mt-2">
                <UserCircle className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#A0A0A0]" />

                <input
                  type="text"
                  value={
                    profileName
                  }
                  onChange={(
                    event
                  ) => {
                    setProfileName(
                      event.target
                        .value
                    );

                    setProfileError(
                      ""
                    );

                    setProfileMessage(
                      ""
                    );
                  }}
                  disabled={
                    profileSaving
                  }
                  maxLength={100}
                  autoComplete="name"
                  required
                  placeholder="Enter your full name"
                  className="w-full rounded-2xl border border-[#DADADA] bg-white py-3.5 pl-12 pr-4 text-sm font-semibold outline-none transition placeholder:font-normal placeholder:text-[#AAAAAA] focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10 disabled:cursor-not-allowed disabled:bg-[#F7F7F7]"
                />
              </div>
            </label>

            <label className="block">
              <span className="text-sm font-black text-[#333333]">
                Email
              </span>

              <div className="relative mt-2">
                <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#A0A0A0]" />

                <input
                  type="email"
                  value={
                    signedInEmail
                  }
                  readOnly
                  className="w-full rounded-2xl border border-[#DADADA] bg-[#F7F7F7] py-3.5 pl-12 pr-4 text-sm font-semibold text-[#757575] outline-none"
                />
              </div>

              <p className="mt-2 text-xs text-[#999999]">
                Your sign-in email cannot
                be changed here.
              </p>
            </label>
          </div>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-[#EFEFEF] pt-5">
            <div>
              <p className="text-xs font-black uppercase tracking-wide text-[#8A8A8A]">
                Partner role
              </p>

              <p className="mt-1 text-sm font-bold">
                {formatRole(
                  access?.partner_role
                )}
              </p>
            </div>

            <button
              type="submit"
              disabled={
                profileSaving ||
                !isProfileDirty
              }
              className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {profileSaving ? (
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}

              {profileSaving
                ? "Saving…"
                : "Save name"}
            </button>
          </div>
        </form>
      </section>

      {canViewOrganisation && (
        <>
          <div className="mt-8">
            <p className="text-xs font-black uppercase tracking-wider text-[#FF914D]">
              Organisation
            </p>

            <h2 className="mt-1 text-2xl font-black tracking-[-0.02em] text-[#333333]">
              Organisation settings
            </h2>

            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-[#757575]">
              Manage the organisation information used across the Partner
              Portal.
            </p>
          </div>

          {message && (
            <div className="mt-5 flex items-start rounded-[22px] border border-green-200 bg-green-50 p-4 text-sm font-semibold text-green-800">
              <BadgeCheck className="mr-3 mt-0.5 h-5 w-5 flex-none" />

              {message}
            </div>
          )}

          {!canManageOrganisation && (
            <div className="mt-5 flex items-start rounded-[22px] border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <LockKeyhole className="mr-3 mt-0.5 h-5 w-5 flex-none" />

              <div>
                <p className="font-black">
                  Read-only access
                </p>

                <p className="mt-1">
                  Only partner owners and
                  administrators can update
                  organisation settings.
                </p>
              </div>
            </div>
          )}

          {settings && (
            <section className="mt-6 grid gap-6 xl:grid-cols-[minmax(0,1.6fr)_minmax(320px,0.8fr)]">
              <form
                onSubmit={
                  handleSubmit
                }
                className="rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm lg:p-8"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                    <Building2 className="h-5 w-5" />
                  </div>

                  <div>
                    <h2 className="text-xl font-black">
                      Organisation profile
                    </h2>

                    <p className="mt-1 text-sm text-[#757575]">
                      Details displayed and
                      used across the Partner
                      Portal.
                    </p>
                  </div>
                </div>

                <fieldset
                  disabled={
                    !canManageOrganisation ||
                    saving
                  }
                  className="mt-7 space-y-6 disabled:opacity-75"
                >
                  <FormField
                    label="Organisation name"
                    name="name"
                    value={
                      draft.name
                    }
                    onChange={
                      handleChange
                    }
                    icon={
                      Building2
                    }
                    required
                    maxLength={150}
                    placeholder="Enter the organisation name"
                  />

                  <TextAreaField
                    label="Organisation description"
                    name="description"
                    value={
                      draft.description
                    }
                    onChange={
                      handleChange
                    }
                    icon={
                      FileText
                    }
                    maxLength={
                      2000
                    }
                    rows={5}
                    placeholder="Provide a short description of the organisation and its programme."
                  />

                  <div className="grid gap-6 md:grid-cols-2">
                    <FormField
                      label="Contact email"
                      name="contact_email"
                      type="email"
                      value={
                        draft.contact_email
                      }
                      onChange={
                        handleChange
                      }
                      icon={
                        Mail
                      }
                      maxLength={
                        320
                      }
                      placeholder="contact@example.com"
                    />

                    <FormField
                      label="Contact phone"
                      name="contact_phone"
                      type="tel"
                      value={
                        draft.contact_phone
                      }
                      onChange={
                        handleChange
                      }
                      icon={
                        Phone
                      }
                      maxLength={
                        50
                      }
                      placeholder="+27 00 000 0000"
                    />
                  </div>

                  <FormField
                    label="Website"
                    name="website"
                    value={
                      draft.website
                    }
                    onChange={
                      handleChange
                    }
                    icon={
                      Globe2
                    }
                    maxLength={
                      500
                    }
                    placeholder="https://example.com"
                  />

                  <TextAreaField
                    label="Organisation address"
                    name="address"
                    value={
                      draft.address
                    }
                    onChange={
                      handleChange
                    }
                    icon={
                      MapPin
                    }
                    maxLength={
                      1000
                    }
                    rows={4}
                    placeholder="Enter the organisation's physical or registered address."
                  />
                </fieldset>

                <div className="mt-8 flex flex-col-reverse gap-3 border-t border-[#EFEFEF] pt-6 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={
                      handleReset
                    }
                    disabled={
                      !canManageOrganisation ||
                      saving ||
                      !isDirty
                    }
                    className="inline-flex items-center justify-center rounded-full border border-[#DADADA] px-5 py-3 text-sm font-black transition hover:border-[#FF914D] hover:bg-[#FFF4EC] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <RotateCcw className="mr-2 h-4 w-4" />

                    Reset changes
                  </button>

                  <button
                    type="submit"
                    disabled={
                      !canManageOrganisation ||
                      saving ||
                      !isDirty
                    }
                    className="inline-flex items-center justify-center rounded-full bg-[#FF914D] px-6 py-3 text-sm font-black text-white transition hover:bg-[#E9782F] disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {saving ? (
                      <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="mr-2 h-4 w-4" />
                    )}

                    {saving
                      ? "Saving…"
                      : "Save settings"}
                  </button>
                </div>
              </form>

              <aside className="space-y-6">
                <article className="rounded-[28px] border border-[#EFEFEF] bg-white p-6 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[#FFF4EC] text-[#FF914D]">
                      <ShieldCheck className="h-5 w-5" />
                    </div>

                    <div>
                      <h2 className="text-lg font-black">
                        Access information
                      </h2>

                      <p className="mt-1 text-xs text-[#757575]">
                        Your current
                        organisation access.
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 space-y-5">
                    <InfoItem
                      label="Your role"
                      value={formatRole(
                        access?.partner_role
                      )}
                      icon={
                        ShieldCheck
                      }
                    />

                    <InfoItem
                      label="Signed-in account"
                      value={
                        signedInEmail ||
                        "Not available"
                      }
                      icon={
                        Mail
                      }
                    />

                    <InfoItem
                      label="Organisation status"
                      value={
                        settings?.status ||
                        "Not available"
                      }
                      icon={BadgeCheck}
                        badgeClassName={
                          settings?.status
                          ? statusClasses(
                            settings.status
                          )
                          :""
                      }
                    />
                  </div>
                </article>

                <article className="hidden">
                  <h2 className="text-lg font-black">
                    Organisation record
                  </h2>

                  <div className="mt-6 space-y-5">
                    <InfoItem
                      label="Organisation ID"
                      value={
                        settings?.id ||
                        "Not available"
                      }
                      icon={
                        Hash
                      }
                      mono
                    />

                    <InfoItem
                      label="Organisation slug"
                      value={
                        settings?.slug ||
                        "Not available"
                      }
                      icon={
                        Hash
                      }
                      mono
                    />

                    <InfoItem
                      label="Created"
                      value={formatDateTime(
                        settings?.created_at
                      )}
                      icon={
                        CalendarDays
                      }
                    />

                    <InfoItem
                      label="Last updated"
                      value={formatDateTime(
                        settings?.updated_at
                      )}
                      icon={
                        CalendarDays
                      }
                    />
                  </div>

                  <div className="mt-6 rounded-2xl bg-[#FFF7ED] p-4 text-xs leading-5 text-[#75523B]">
                    The organisation ID,
                    slug and status are
                    managed by Gwin and
                    cannot be changed from
                    this page.
                  </div>
                </article>
              </aside>
            </section>
          )}
        </>
      )}
    </>
  );
}

function FormField({
  label,
  icon: Icon,
  ...inputProps
}) {
  return (
    <label className="block">
      <span className="text-sm font-black text-[#333333]">
        {label}

        {inputProps.required && (
          <span className="ml-1 text-red-600">
            *
          </span>
        )}
      </span>

      <div className="relative mt-2">
        <Icon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#A0A0A0]" />

        <input
          {...inputProps}
          className="w-full rounded-2xl border border-[#DADADA] bg-white py-3.5 pl-12 pr-4 text-sm font-semibold outline-none transition placeholder:font-normal placeholder:text-[#AAAAAA] focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10 disabled:cursor-not-allowed disabled:bg-[#F7F7F7]"
        />
      </div>
    </label>
  );
}

function TextAreaField({
  label,
  icon: Icon,
  ...textAreaProps
}) {
  return (
    <label className="block">
      <span className="text-sm font-black text-[#333333]">
        {label}
      </span>

      <div className="relative mt-2">
        <Icon className="pointer-events-none absolute left-4 top-4 h-5 w-5 text-[#A0A0A0]" />

        <textarea
          {...textAreaProps}
          className="w-full resize-y rounded-2xl border border-[#DADADA] bg-white py-3.5 pl-12 pr-4 text-sm font-semibold outline-none transition placeholder:font-normal placeholder:text-[#AAAAAA] focus:border-[#FF914D] focus:ring-4 focus:ring-[#FF914D]/10 disabled:cursor-not-allowed disabled:bg-[#F7F7F7]"
        />
      </div>

      {textAreaProps.maxLength && (
        <p className="mt-1 text-right text-xs text-[#999999]">
          {String(
            textAreaProps.value ||
              ""
          ).length}
          /
          {
            textAreaProps.maxLength
          }
        </p>
      )}
    </label>
  );
}

function InfoItem({
  label,
  value,
  icon: Icon,
  mono = false,
  badgeClassName = "",
}) {
  return (
    <div className="flex items-start gap-3">
      <div className="mt-0.5 flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-[#FFF4EC] text-[#FF914D]">
        <Icon className="h-4 w-4" />
      </div>

      <div className="min-w-0">
        <p className="text-xs font-black uppercase tracking-wide text-[#8A8A8A]">
          {label}
        </p>

        {badgeClassName ? (
          <span
            className={[
              "mt-2 inline-flex rounded-full px-3 py-1 text-xs font-black capitalize",
              badgeClassName,
            ].join(" ")}
          >
            {value}
          </span>
        ) : (
          <p
            className={[
              "mt-1 break-words text-sm font-bold text-[#333333]",
              mono
                ? "font-mono text-xs"
                : "",
            ].join(" ")}
          >
            {value}
          </p>
        )}
      </div>
    </div>
  );
}